const STORAGE_KEY = 'rikosub:recentVideos';
export const RECENT_VIDEOS_MAX = 8;

export type RecentVideo = {
  id: string;
  openedAt: number;
  /** Display title when known (from YouTube player getVideoData). */
  title?: string;
};

/** In-memory fallback when localStorage is unavailable (e.g. Vitest node). */
const memoryStore = new Map<string, string>();

function storageGet(key: string): string | null {
  if (typeof localStorage !== 'undefined') {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      // fall through to memory
    }
  }
  memoryStore.set(key, value);
}

function storageRemove(key: string): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
  memoryStore.delete(key);
}

/** Test helper — clears persisted recent videos. */
export function clearRecentVideos(): void {
  storageRemove(STORAGE_KEY);
}

function readRaw(): RecentVideo[] {
  try {
    const raw = storageGet(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is RecentVideo =>
        Boolean(item)
        && typeof item === 'object'
        && typeof (item as RecentVideo).id === 'string'
        && typeof (item as RecentVideo).openedAt === 'number',
      )
      .map((item) => {
        const title =
          typeof item.title === 'string' && item.title.trim() ? item.title.trim() : undefined;
        return title ? { id: item.id, openedAt: item.openedAt, title } : { id: item.id, openedAt: item.openedAt };
      })
      .slice(0, RECENT_VIDEOS_MAX);
  } catch {
    return [];
  }
}

export function listRecentVideos(): RecentVideo[] {
  return readRaw();
}

/**
 * Insert / bump a video to the front of recent history.
 * Keeps a prior title when `title` is omitted (re-open before player metadata arrives).
 */
export function pushRecentVideo(
  id: string,
  now = Date.now(),
  title?: string,
): RecentVideo[] {
  const previous = readRaw();
  const prior = previous.find((v) => v.id === id);
  const nextTitle = (title?.trim() || prior?.title)?.trim();
  const entry: RecentVideo = nextTitle
    ? { id, openedAt: now, title: nextTitle }
    : { id, openedAt: now };
  const next = [entry, ...previous.filter((v) => v.id !== id)].slice(0, RECENT_VIDEOS_MAX);
  storageSet(STORAGE_KEY, JSON.stringify(next));
  return next;
}

/** Patch title for an existing recent entry (does not change order). */
export function updateRecentVideoTitle(id: string, title: string): RecentVideo[] {
  const trimmed = title.trim();
  if (!trimmed) return readRaw();
  const previous = readRaw();
  const idx = previous.findIndex((v) => v.id === id);
  if (idx < 0) return pushRecentVideo(id, Date.now(), trimmed);
  if (previous[idx]?.title === trimmed) return previous;
  const next = previous.map((v) => (v.id === id ? { ...v, title: trimmed } : v));
  storageSet(STORAGE_KEY, JSON.stringify(next));
  return next;
}
